import React, { useEffect, useState } from 'react';
import { Checkbox, ColorPicker } from '../shape.map';
import styles from '../../../styles/global.module.css';
import BgColorIcon from '@/assets/svg/bg_color_icon.svg';
import type { ShowColorOption } from '@/types/shape';
import { ShapeType } from '@/types/shape';
import { GANN_FAN_LABELS } from '@/types/shape';

interface Props {
  value: ShowColorOption | ShowColorOption[];
  onChange: (val: ShowColorOption | ShowColorOption[]) => void;
  label?: string;
  disabled?: boolean;
  type: ShapeType;
  isFromMiniToolbar: boolean;
}

const ShowColorOptionComponent: React.FC<Props> = ({
  value,
  onChange,
  label = 'Show',
  disabled = false,
  type,
  isFromMiniToolbar,
}) => {
  const isGannFan = type === ShapeType.GANN_FAN;

  const [fanOptions, setFanOptions] = useState<ShowColorOption[]>([]);

  // Initialise the nine rows if they don’t arrive from props
  useEffect(() => {
    if (!isGannFan) return;

    if (Array.isArray(value) && value.length === 9) {
      setFanOptions(value);
    }
  }, [isGannFan, value]);

  const updateFanRow = (idx: number, updated: Partial<ShowColorOption>) => {
    const next = [...fanOptions];
    next[idx] = { ...next[idx], ...updated };
    setFanOptions(next);
    onChange(next);
  };

  const renderColorOption = (
    show: boolean,
    color: string,
    onShowChange: (show: boolean) => void,
    onColorChange: (color: string) => void,
    isDisabled: boolean,
    labelText?: string,
  ) => {
    const showLabel =
      (type === ShapeType.TEXT ||
        type === ShapeType.RECTANGLE ||
        type === ShapeType.TRIANGLE ||
        type === ShapeType.PARALLEL_CHANNEL ||
        type === ShapeType.FIXED_RANGE_VOLUME_PROFILE ||
        type === ShapeType.ANCHORED_VOLUME_PROFILE) &&
      !isFromMiniToolbar;

    const content = (
      <>
        {label !== 'backgroundWithoutCheckbox' && <Checkbox value={show} onChange={onShowChange} />}
        {type === ShapeType.GANN_FAN && labelText && <label className="min-w-[40px]">{labelText}</label>}
        <ColorPicker
          value={color}
          onChange={onColorChange}
          disabled={show === false || isDisabled}
          isFromMiniToolbar={isFromMiniToolbar}
          svgIcon={BgColorIcon}
          // label={label}
        />
      </>
    );

    if (isFromMiniToolbar) {
      return <div className={`flex items-center ${styles.opacity_option_wrapper}`}>{content}</div>;
    }

    if (showLabel) {
      return (
        <div className={styles.property_row}>
          <label className={styles.property_label}>{label}</label>
          <div className={styles.property_value}>{content}</div>
        </div>
      );
    }

    return <div className={`flex items-center ${styles.opacity_option_wrapper}`}>{content}</div>;
  };

  if (isGannFan) {
    return (
      <div className="grid grid-cols-2 gap-4" style={{ gridColumn: 'span 2' }}>
        {fanOptions.map((opt, i) => (
          <div key={i} className="flex flex-col space-y-4">
            <div className={`flex items-center space-x-4`}>
              {renderColorOption(
                opt.show,
                opt.color,
                show => updateFanRow(i, { show }),
                color => updateFanRow(i, { color }),
                disabled,
                GANN_FAN_LABELS[i],
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  const single = value as ShowColorOption;
  if (!single) {
    return null;
  }
  return renderColorOption(
    single.show,
    single.color,
    show => onChange({ ...single, show }),
    color => onChange({ ...single, color }),
    disabled,
  );
};

export default ShowColorOptionComponent;
